import { useEffect, useState } from "react";
import axios from "axios";
import { Briefcase, Mail, MapPin, Clock, CalendarDays, ChevronDown, ChevronUp } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import JobApplicationForm from "../components/JobApplicationForm";
import { setJsonLd, SEO_BASE } from "../lib/seo";

const EMPLOYMENT_TYPE_MAP = {
  "Full-time": "FULL_TIME",
  "Part-time": "PART_TIME",
  Contract: "CONTRACTOR",
  Internship: "INTERN",
};

const API = process.env.REACT_APP_BACKEND_URL;

const NoOpenings = () => (
  <div
    className="mx-auto max-w-2xl border border-border bg-brand-surface p-10 text-center shadow-sm lg:p-14"
    data-testid="careers-no-openings"
  >
    <div className="mx-auto flex h-14 w-14 items-center justify-center border border-border bg-white">
      <Briefcase className="h-6 w-6 text-brand-red" strokeWidth={1.5} aria-hidden="true" />
    </div>
    <h2 className="mt-6 text-2xl font-semibold">Current Openings</h2>
    <p className="mt-4 text-base leading-relaxed text-brand-grey">
      There are currently no published openings. Please check again later for future opportunities.
    </p>
    <p className="mt-6 text-sm text-brand-grey">
      If you would like to be considered for future roles, you may share your profile at{" "}
      <a
        href="mailto:careers@vaskiontech.com"
        className="font-medium text-brand-red underline underline-offset-2 transition-colors duration-200 hover:text-brand-redDark"
        data-testid="careers-email-link"
      >
        careers@vaskiontech.com
      </a>
      .
    </p>
    <div className="mt-8 inline-flex items-center gap-2 text-xs text-neutral-500">
      <Mail className="h-3.5 w-3.5" aria-hidden="true" />
      Applications are reviewed when new positions open.
    </div>
  </div>
);

const JobCard = ({ job }) => {
  const [open, setOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  return (
    <div className="border border-border bg-white shadow-sm" data-testid={`careers-job-${job.id}`}>
      <div className="p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-red">{job.department}</p>
            <h3 className="mt-1 text-xl font-semibold text-brand-graphite" data-testid={`careers-job-title-${job.id}`}>
              {job.title}
            </h3>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-brand-grey">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-brand-red" aria-hidden="true" /> {job.location}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-brand-red" aria-hidden="true" /> {job.employmentType} · {job.experience}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5 text-brand-red" aria-hidden="true" /> Apply by {job.deadline}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="btn-secondary flex items-center gap-2 text-sm"
            onClick={() => setOpen(!open)}
            data-testid={`careers-job-toggle-${job.id}`}
          >
            {open ? "Hide Details" : "View Details"}
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {job.skills.map((s) => (
            <span key={s} className="bg-brand-surface px-2.5 py-1 text-xs font-medium text-brand-graphite">
              {s}
            </span>
          ))}
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-brand-surface p-6 sm:p-8" data-testid={`careers-job-details-${job.id}`}>
          <div className="space-y-6 text-sm leading-relaxed text-brand-graphite">
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-grey">About the Role</h4>
              <p className="mt-2 whitespace-pre-wrap">{job.description}</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-grey">Responsibilities</h4>
              <p className="mt-2 whitespace-pre-wrap">{job.responsibilities}</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-grey">Qualifications</h4>
              <p className="mt-2 whitespace-pre-wrap">{job.qualifications}</p>
            </div>
          </div>
          {!applying && (
            <button
              type="button"
              className="btn-primary mt-8"
              onClick={() => setApplying(true)}
              data-testid={`careers-job-apply-${job.id}`}
            >
              Apply for this Position
            </button>
          )}
        </div>
      )}
      {open && applying && <JobApplicationForm job={job} />}
    </div>
  );
};

const Careers = () => {
  const [jobs, setJobs] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/api/careers/jobs`)
      .then((res) => setJobs(res.data.items))
      .catch(() => setJobs([]));
  }, []);

  useEffect(() => {
    if (!jobs || jobs.length === 0) {
      setJsonLd("jobs-jsonld", null);
      return;
    }
    setJsonLd("jobs-jsonld", jobs.map((job) => ({
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: job.title,
      description: job.description,
      datePosted: job.createdAt,
      validThrough: `${job.deadline}T23:59:59+05:30`,
      employmentType: EMPLOYMENT_TYPE_MAP[job.employmentType] || "FULL_TIME",
      hiringOrganization: {
        "@type": "Organization",
        name: "Vaskion Technologies Private Limited",
        sameAs: SEO_BASE,
        logo: `${SEO_BASE}/assets/vaskion-logo.png`,
      },
      jobLocation: {
        "@type": "Place",
        address: { "@type": "PostalAddress", addressLocality: job.location, addressCountry: "IN" },
      },
      experienceRequirements: job.experience,
      skills: job.skills.join(", "),
      directApply: true,
      url: `${SEO_BASE}/careers`,
    })));
    return () => setJsonLd("jobs-jsonld", null);
  }, [jobs]);

  return (
    <main data-testid="careers-page">
      <PageHeader
        eyebrow="Careers"
        title="Build Your Career at Vaskion"
        description="We are always interested in meeting thoughtful engineers, designers and problem-solvers who care about quality."
      />
      <section className="bg-white py-20 lg:py-28">
        <div className="container-site">
          {jobs === null ? (
            <p className="py-10 text-center text-sm text-brand-grey">Loading openings…</p>
          ) : jobs.length === 0 ? (
            <Reveal>
              <NoOpenings />
            </Reveal>
          ) : (
            <div className="mx-auto max-w-4xl">
              <h2 className="text-2xl font-semibold" data-testid="careers-openings-heading">
                Current Openings <span className="text-brand-red">({jobs.length})</span>
              </h2>
              <div className="mt-8 space-y-6" data-testid="careers-jobs-list">
                {jobs.map((job) => (
                  <Reveal key={job.id}>
                    <JobCard job={job} />
                  </Reveal>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default Careers;
